import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks';
import { Search, Menu, X, User, LogOut, Calendar, NotepadText } from 'lucide-react';
import { Icon } from '../../icons';

export const NavBar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
    navigate('/');
  };

  const isAdmin = user?.userType === 'admin';

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="container flex items-center justify-between px-4 py-4 mx-auto">
        {/* Logo */}
        <Link to="/" className="flex items-center flex-1">
          <div className="flex items-center">
            <span className="text-2xl font-bold text-blue-600">fixionity</span>
          </div>
        </Link>

        {/* Search bar - hidden on mobile */}
        <div className="items-center justify-center flex-1 hidden max-w-md mx-4 md:flex">
          <div className="relative w-full search-container">
            <input
              type="text"
              placeholder="Buscar equipos de rehabilitación..."
              className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Search
              size={18}
              className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
            />
          </div>
        </div>

        {/* Navigation Links - desktop */}
        <nav className="items-center justify-end flex-1 hidden space-x-2 md:flex">
          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={toggleProfileMenu}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <div className="w-8 h-8 overflow-hidden bg-gray-200 rounded-full">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="object-cover w-full h-full cursor-pointer"
                    />
                  ) : (
                    <User size={32} className="p-1 text-gray-600" />
                  )}
                </div>
              </button>

              {isProfileMenuOpen && (
                <div className="absolute right-0 z-50 w-48 py-1 mt-2 bg-white rounded-md shadow-lg fade-in">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Perfil
                  </Link>
                  <Link
                    to="/bookings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileMenuOpen(false)}
                  >
                    Mis Reservas
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      Panel de Administración
                    </Link>
                  )}
                  <button
                    className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100"
                    onClick={handleLogout}
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="font-medium text-gray-700 hover:text-primary"
              >
                Iniciar Sesión
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 text-white transition rounded-lg bg-primary hover:bg-primary/90"
              >
                Registrarse
              </Link>
            </div>

          )}
          <Link to="/search" className="font-medium text-gray-700 hover:text-primary">
            <Icon name="whatsapp" size={40} title="WhatsApp" className="text-green-700" />
          </Link>
        </nav>

        {/* Mobile menu button */}
        <button
          className="text-gray-500 md:hidden focus:outline-none"
          onClick={toggleMenu}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="px-4 py-4 bg-white shadow-md md:hidden fade-in">
          <div className="space-y-4">
            <div className="relative search-container">
              <input
                type="text"
                placeholder="Buscar equipos de rehabilitación..."
                className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-full focus:outline-none focus:border-transparent"
              />
              <Search
                size={18}
                className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
              />
            </div>

            <Link
              to="/search"
              className="block py-2 text-gray-700 hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              Buscar Equipos
            </Link>

            {isAuthenticated ? (
              <>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center py-2 space-x-3">
                    <div className="w-8 h-8 overflow-hidden bg-gray-200 rounded-full">
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <User size={32} className="p-1 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center py-2 space-x-2 text-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User size={18} />
                    <span>Perfil</span>
                  </Link>
                  <Link
                    to="/bookings"
                    className="flex items-center py-2 space-x-2 text-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Calendar size={18} />
                    <span>Mis Reservas</span>
                  </Link>
                   {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="flex items-center py-2 space-x-2 text-gray-700"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <NotepadText size={18} />
                      <span>Panel de Administración</span>
                    </Link>
                  )}
                  <button
                    className="flex items-center w-full py-2 space-x-2 text-left text-red-600"
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut size={18} />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col pt-2 space-y-2 border-t border-gray-200">
                <Link
                  to="/login"
                  className="py-2 text-gray-700 hover:text-primary"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Iniciar Sesión
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 text-center text-white transition rounded-lg bg-primary hover:bg-primary/90"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Registrarse
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};